import type { DetailContext } from './types';

interface Props {
  photos: string[];
  /** Used to build each image's alt text: "<name> – 1". */
  name: string;
  ctx: DetailContext;
}

/**
 * Photo gallery with thumbnail strip. Renders static markup only — the slide
 * transitions are wired up by `attachDetailBehavior`, which finds these
 * elements through their `data-vp-*` attributes.
 */
export default function PhotoCarousel({ photos, name, ctx }: Props) {
  if (photos.length === 0) return null;
  const { locale, imageUrl } = ctx;
  const many = photos.length > 1;

  return (
    <div className="vp-photos-section">
      <div className="vp-carousel-wrap" data-vp-carousel data-photo-count={photos.length}>
        <div className="vp-carousel-track" data-vp-track>
          {photos.map((photoId, i) => (
            <div className="vp-carousel-slide" key={photoId}>
              <img
                src={imageUrl(photoId, 'public')}
                alt={`${name} – ${i + 1}`}
                loading={i === 0 ? 'eager' : 'lazy'}
              />
            </div>
          ))}
        </div>

        {many && (
          <>
            <button
              className="vp-carousel-btn vp-prev"
              data-vp-prev
              type="button"
              aria-label={locale === 'vi' ? 'Ảnh trước' : 'Previous photo'}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <path d="M11 4L6 9l5 5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              className="vp-carousel-btn vp-next"
              data-vp-next
              type="button"
              aria-label={locale === 'vi' ? 'Ảnh sau' : 'Next photo'}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <path d="M7 4l5 5-5 5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </>
        )}
      </div>

      {many && (
        <div
          className="vp-thumb-row"
          role="tablist"
          aria-label={locale === 'vi' ? 'Bộ sưu tập ảnh' : 'Photo gallery'}
        >
          {photos.map((photoId, i) => (
            <button
              key={photoId}
              type="button"
              className={`vp-thumb${i === 0 ? ' vp-thumb-active' : ''}`}
              data-vp-thumb
              data-index={i}
              role="tab"
              aria-label={`${locale === 'vi' ? 'Ảnh' : 'Photo'} ${i + 1}`}
              aria-selected={i === 0}
            >
              <img src={imageUrl(photoId, 'thumbnail')} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
